<template>
  <div>
    <CCard>
      <div
        v-if="isLoading"
        :style="{ height: '80vh' }"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <CSpinner
          style="width:4rem;height:4rem;"
          color="info"
          grow
        />
      </div>
      <div v-else>
        <CCardHeader>
          <CIcon
            name="cil-chart"
            class="mr-1"
          /> Seller Offers Insights
          <CButton
            class="float-right d-flex align-items-center justify-content-center"
            size="sm"
            color="secondary"
            @click="$router.push({ name: 'Seller Offers' })"
          >
            <CIcon
              size="sm"
              name="cil-arrow-left"
              class="mr-1"
            /> Back to Offers
          </CButton>
        </CCardHeader>
        <CCardBody>
          <CCallout color="info">
            <small class="text-muted">Total Offers with Insights:</small><br>
            <strong class="h4">{{ offerInsights.length }}</strong>
            <div
              v-if="formattedLastUpdated"
              class="text-muted small mt-1"
            >
              Last updated at {{ formattedLastUpdated }}
            </div>
          </CCallout>
          <CDataTable
            hover
            striped
            border
            small
            :items="enrichedInsights"
            :fields="fields"
            :items-per-page="20"
            :pagination="{ align: 'center' }"
            column-filter
            sorter
          >
            <template #offerName="{ item }">
              <td>
                <router-link
                  :to="{ name: 'SellerOfferEdit', params: { id: item.id } }"
                  target="_blank"
                >
                  {{ item.offerName }}
                </router-link>
              </td>
            </template>

            <template #categoryName="{ item }">
              <td>{{ item.categoryName }}</td>
            </template>

            <template #languageName="{ item }">
              <td>{{ item.languageName }}</td>
            </template>

            <template #schedule="{ item }">
              <td style="max-width: 500px;">
                <span
                  v-if="!item.schedule || item.schedule.length === 0"
                  class="text-muted font-italic"
                >No demand</span>
                <div
                  v-for="(schedule, idx) in item.schedule"
                  v-else
                  :key="idx"
                  class="schedule-item mb-2 p-2 border rounded bg-light"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="mb-1">
                        <strong class="small">Hours:</strong>
                        <span
                          v-if="schedule.isAllHours"
                          class="badge badge-success ml-1"
                        >24/7</span>
                        <span
                          v-else
                          class="small ml-1"
                        >
                          {{ formatDailySchedule(schedule.dailySchedule) }}
                        </span>
                      </div>
                      <div class="mb-1">
                        <strong class="small">Location:</strong>
                        <span
                          v-if="schedule.location && schedule.location.isNationwide"
                          class="badge badge-primary ml-1"
                        >Nationwide</span>
                        <span
                          v-else
                          class="small ml-1"
                          :title="getStatesTitle(schedule.location)"
                        >
                          {{ formatStates(schedule.location && schedule.location.states) }}
                        </span>
                      </div>
                      <div>
                        <strong class="small">Sources:</strong>
                        <span
                          class="small ml-1"
                          :title="(schedule.trafficSourceNames || []).join(', ')"
                        >
                          {{ formatTrafficSources(schedule.trafficSourceNames) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </template>
          </CDataTable>
        </CCardBody>
      </div>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default {
  name: 'SellerOffersInsights',

  title: 'Seller Offers Insights',

  data() {
    return {
      isLoading: false,
      fields: [
        { key: 'offerName', label: 'Seller Offer', sortable: true, _style: 'width: 220px;' },
        { key: 'categoryName', label: 'Category', sortable: true },
        { key: 'languageName', label: 'Language', sortable: true },
        { key: 'schedule', label: 'Demand', filter: false, sorter: false }
      ]
    }
  },

  computed: {
    ...mapState('insights', ['offerInsights']),
    ...mapState('sellerOffer', ['sellerOffers']),

    enrichedInsights() {
      return this.offerInsights.map(item => {
        const offer = this.sellerOffers.find(o => o.id === item.id)
        return {
          ...item,
          offerName: offer && offer.name ? offer.name : item.id
        }
      })
    },

    earliestUpdatedAt() {
      if (!this.offerInsights || this.offerInsights.length === 0) {
        return null
      }

      const dates = this.offerInsights
        .filter(item => item.updatedAt)
        .map(item => new Date(item.updatedAt))
      if (dates.length === 0) {
        return null
      }

      return new Date(Math.min(...dates))
    },

    formattedLastUpdated() {
      if (!this.earliestUpdatedAt) {
        return null
      }

      const date = this.earliestUpdatedAt
      const month = date.toLocaleString('en-US', { month: 'short' })
      const day = date.getDate()
      const year = date.getFullYear()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${month} ${day}, ${year} ${hours}:${minutes}`
    }
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([
        this.fetchAllOfferInsights(),
        this.listSellerOffers({})
      ])
    } catch (error) {
      // Error handled by API layer
    } finally {
      this.isLoading = false
    }
  },

  destroyed() {
    this.$store.dispatch('insights/reset')
  },

  methods: {
    ...mapActions('insights', ['fetchAllOfferInsights']),
    ...mapActions('sellerOffer', { listSellerOffers: 'list' }),

    formatTime(time) {
      if (!time) {
        return ''
      }

      const hour = time.hour.toString().padStart(2, '0')
      const minute = time.minute.toString().padStart(2, '0')
      return `${hour}:${minute}`
    },

    formatDailySchedule(dailySchedule) {
      if (!dailySchedule || dailySchedule.length === 0) {
        return 'N/A'
      }

      const scheduleMap = {}
      dailySchedule.forEach((day, idx) => {
        if (idx >= DAY_NAMES.length) {
          return
        }

        const dayName = DAY_NAMES[idx]
        if (day.isClosed) {
          scheduleMap[dayName] = 'Closed'
        } else {
          const open = this.formatTime(day.openTime)
          const close = this.formatTime(day.closeTime)
          scheduleMap[dayName] = `${open}-${close}`
        }
      })

      // Group days with same schedule
      const groups = []
      let currentGroup = { days: [], schedule: null }

      for (const day of DAY_NAMES) {
        const sched = scheduleMap[day]
        if (sched === currentGroup.schedule) {
          currentGroup.days.push(day)
        } else {
          if (currentGroup.days.length > 0) {
            groups.push({ ...currentGroup })
          }

          currentGroup = { days: [day], schedule: sched }
        }
      }
      
      if (currentGroup.days.length > 0) {
        groups.push(currentGroup)
      }

      return groups.map(g => {
        const dayRange = g.days.length > 1
          ? `${g.days[0]}-${g.days[g.days.length - 1]}`
          : g.days[0]
        return `${dayRange}: ${g.schedule}`
      }).join(' | ')
    },

    getStatesTitle(location) {
      const states = location && location.states ? location.states : []
      return states.join(', ')
    },

    formatStates(states) {
      if (!states || states.length === 0) {
        return 'None'
      }

      return states.join(', ')
    },

    formatTrafficSources(sources) {
      if (!sources || sources.length === 0) {
        return 'None'
      }

      return sources.join(', ')
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule-item {
  font-size: 0.8rem;
}

.schedule-item:last-child {
  margin-bottom: 0 !important;
}
</style>
